import { Pipe, PipeTransform } from '@angular/core';
import { format, isYesterday, Locale } from 'date-fns';
import { enUS, de, fr, it } from 'date-fns/locale';
import { TranslateService } from '@ngx-translate/core';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';

const SUPPORTED_LOCALES: Record<string, Locale> = {
    'en': enUS,
    'en-US': enUS,
    de,
    'de-DE': de,
    fr,
    'fr-FR': fr,
    it,
    'it-IT': it
};

@Pipe({
    name: 'lastActiveLabel', pure: false,
    standalone: false
}) // Change to false
export class LastActiveLabelPipe implements PipeTransform {
    constructor(private translateService: TranslateService) {}

    transform(inputDate: Date | string | number | null | undefined): string {
        if (!inputDate) return '';

        const targetDate = new Date(inputDate);
        if (isNaN(targetDate.getTime())) return '';

        // Check for invalid date
        let absoluteDate = new Date('2010-01-01');
        let currentDate = targetDate.toISOString();
        if (currentDate <= absoluteDate.toISOString()) {
            return 'N/A';
        }

        // Get current language (will be re-evaluated on language change)
        const currentLanguage = this.translateService.currentLang || 'en';
        const dateLocale = SUPPORTED_LOCALES[currentLanguage] ?? enUS;

        // Use UTC time for accurate comparison
        const currentTimeUTC = new Date();
        const timeDifferenceMs = currentTimeUTC.getTime() - targetDate.getTime();

        if (timeDifferenceMs < 0) {
            return this.capitalizeFirstLetter(format(targetDate, 'MMM d, yyyy', { locale: dateLocale }));
        }

        if (timeDifferenceMs < PCXConstants.UserActivity.PERIODIC_ACTIVE_TIME_IN_MILLISECONDS) {
            return this.getTranslation(PCXConstants.UserActivity.ACTIVE_NOW);
        }
        const minutesAgo = Math.floor(timeDifferenceMs / 60_000);

        // Show minutes if less than 1 hour
        if (minutesAgo < 60) {
            return `${minutesAgo} ${this.getTranslation(PCXConstants.UserActivity.MIN)} ${this.getTranslation(
                PCXConstants.UserActivity.AGO
            )}`;
        }

        const hoursAgo = Math.floor(minutesAgo / 60);
        if (hoursAgo < 24)
            return `${hoursAgo} ${
                hoursAgo === 1
                    ? this.getTranslation(PCXConstants.UserActivity.HOUR)
                    : this.getTranslation(PCXConstants.UserActivity.HOURS)
            } ${this.getTranslation(PCXConstants.UserActivity.AGO)}`;

        if (isYesterday(targetDate)) {
            return this.getTranslation(PCXConstants.UserActivity.YESTERDAY);
        }
        return this.capitalizeFirstLetter(format(targetDate, 'MMM d, yyyy', { locale: dateLocale }));
    }

    private getTranslation(translationKey: string): string {
        return (
            this.translateService.instant(`${PCXConstants.UserActivity.LAST_ACTIVE_TEXT}.${translationKey}`) ||
            translationKey
        );
    }

    private capitalizeFirstLetter(text: string): string {
        if (!text) return text;
        return text.charAt(0).toUpperCase() + text.slice(1);
    }
}
